import { useCallback, useEffect, useState } from "react";
import { useViewerActions, useViewerStore } from "../stores/viewerStore";
import {
  DEFAULT_INERTIAL_VISIBILITY_UI_STATE,
  dispatchInertialVisibilityUiState,
  type InertialVisibilityUiState,
} from "./visibilityEvents";

export type InertialVisibilityState = InertialVisibilityUiState;

const defaultState = (): InertialVisibilityState => ({
  ...DEFAULT_INERTIAL_VISIBILITY_UI_STATE,
});

let rememberedInertialVisibilityState: InertialVisibilityState = defaultState();

export type UseInertialVisibilityControlsArgs = {
  inertialUuid: string | null;
  enabled?: boolean;
  initialState?: Partial<InertialVisibilityState>;
  onStateChange?: (state: InertialVisibilityState) => void;
};

export type UseInertialVisibilityControlsResult = {
  state: InertialVisibilityState;
  disabled: boolean;
  focusDisabled: boolean;
  toggleFocus: () => void;
  toggleOrientationAxes: () => void;
  reset: () => void;
};

export function useInertialVisibilityControls({
  inertialUuid,
  enabled = true,
  initialState,
  onStateChange,
}: UseInertialVisibilityControlsArgs): UseInertialVisibilityControlsResult {
  const showInertials = useViewerStore((s) => s.showInertials);
  const viewerActions = useViewerActions();
  const [state, setStateInternal] = useState<InertialVisibilityState>(() => ({
    ...defaultState(),
    ...rememberedInertialVisibilityState,
    ...initialState,
  }));

  const disabled = !enabled || !inertialUuid;
  const focusDisabled = disabled;

  const setState = useCallback(
    (patch: Partial<InertialVisibilityState>) => {
      setStateInternal((prev) => {
        const next = { ...prev, ...patch };
        rememberedInertialVisibilityState = next;
        onStateChange?.(next);
        return next;
      });
    },
    [onStateChange]
  );

  const ensureInertialsVisible = useCallback(() => {
    if (!showInertials) {
      viewerActions.toggleShowInertials();
    }
  }, [showInertials, viewerActions]);

  useEffect(() => {
    dispatchInertialVisibilityUiState({
      focus: state.focus,
      orientationAxes: state.orientationAxes,
    });
  }, [state.focus, state.orientationAxes]);

  useEffect(() => {
    return () => {
      dispatchInertialVisibilityUiState(DEFAULT_INERTIAL_VISIBILITY_UI_STATE);
    };
  }, []);

  const toggleFocus = useCallback(() => {
    if (focusDisabled) return;
    const next = !state.focus;
    if (next) ensureInertialsVisible();
    setState({ focus: next });
  }, [focusDisabled, ensureInertialsVisible, setState, state.focus]);

  const toggleOrientationAxes = useCallback(() => {
    if (disabled) return;
    const next = !state.orientationAxes;
    if (next) ensureInertialsVisible();
    setState({ orientationAxes: next });
  }, [disabled, ensureInertialsVisible, setState, state.orientationAxes]);

  const reset = useCallback(() => {
    dispatchInertialVisibilityUiState(DEFAULT_INERTIAL_VISIBILITY_UI_STATE);
    setStateInternal(() => {
      const next = { ...defaultState(), ...initialState };
      rememberedInertialVisibilityState = next;
      onStateChange?.(next);
      return next;
    });
  }, [initialState, onStateChange]);

  return {
    state,
    disabled,
    focusDisabled,
    toggleFocus,
    toggleOrientationAxes,
    reset,
  };
}
